import { useIndicatorsSummary } from '@/api/hooks';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { PageHeader } from '@/components/ui/PageHeader';
import { usePropertyScope } from '@/contexts/PropertyContext';
import { formatDate } from '@/lib/format';
import { Link } from 'react-router-dom';
import { useMemo, useState } from 'react';

function formatGmd(value: number | null | undefined): string {
  if (value == null) return '—';
  return `${value.toFixed(2)} kg/dia`;
}

function formatRate(value: number | null | undefined): string {
  if (value == null) return '—';
  return `${(value * 100).toFixed(1)}%`;
}

function formatDays(value: number | null | undefined): string {
  if (value == null) return '—';
  return `${Math.round(value)} dias`;
}

function formatWeight(value: number | null | undefined): string {
  if (value == null) return '—';
  return `${value.toFixed(1)} kg`;
}

export function IndicatorsDashboardPage() {
  const { propertyId } = usePropertyScope();
  const [from, setFrom] = useState(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 1);
    return d.toISOString().slice(0, 10);
  });
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));

  const { data, isLoading } = useIndicatorsSummary(propertyId, from, to);

  const periodLabel = useMemo(() => {
    if (!data?.period) return '';
    return `${formatDate(data.period.from)} — ${formatDate(data.period.to)}`;
  }, [data?.period]);

  return (
    <div>
      <PageHeader
        title="Indicadores Zootécnicos"
        subtitle="GMD, taxa de prenhez, IEP e desmame no período selecionado"
      />

      {!propertyId ? (
        <Card>
          <CardBody className="py-12 text-center text-manejai-bark/60">
            Selecione uma propriedade para ver os indicadores.
          </CardBody>
        </Card>
      ) : (
        <>
          <div className="mb-6 flex flex-wrap items-end gap-3">
            <label className="text-sm">
              <span className="mb-1 block text-xs text-manejai-bark/60">De</span>
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="rounded-xl border border-manejai-sand px-3 py-2 text-sm"
              />
            </label>
            <label className="text-sm">
              <span className="mb-1 block text-xs text-manejai-bark/60">Até</span>
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="rounded-xl border border-manejai-sand px-3 py-2 text-sm"
              />
            </label>
            {periodLabel && (
              <p className="text-xs text-manejai-bark/55 pb-2">Período analisado: {periodLabel}</p>
            )}
          </div>

          {isLoading ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-24 rounded-2xl bg-manejai-sand/40 animate-pulse" />
              ))}
            </div>
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-6">
                <Metric
                  label="GMD médio do rebanho"
                  value={formatGmd(data?.growth.avg_gmd_kg_per_day)}
                  hint={`${data?.growth.animals_with_gmd ?? 0} animais com GMD`}
                />
                <Metric
                  label="Taxa de prenhez (coberturas)"
                  value={formatRate(data?.reproduction.pregnancy_rate_by_cover)}
                  hint={`${data?.reproduction.covers_pregnant ?? 0}/${data?.reproduction.covers_total ?? 0} coberturas`}
                />
                <Metric
                  label="IEP médio"
                  value={formatDays(data?.reproduction.avg_iep_days)}
                  hint={`${data?.reproduction.cows_with_iep_sample ?? 0} intervalos`}
                />
                <Metric
                  label="Peso médio desmame"
                  value={formatWeight(data?.weaning.avg_weaning_weight_kg)}
                  hint={`${data?.weaning.weanings_count ?? 0} desmames`}
                />
              </div>

              <div className="grid gap-6 lg:grid-cols-2">
                <Card>
                  <CardHeader>
                    <h2 className="font-display font-semibold text-manejai-forest">Crescimento</h2>
                  </CardHeader>
                  <CardBody className="space-y-3 text-sm">
                    <Row label="GMD mediano" value={formatGmd(data?.growth.median_gmd_kg_per_day)} />
                    <Row
                      label="Animais com GMD no período"
                      value={String(data?.growth.animals_with_gmd ?? 0)}
                    />
                  </CardBody>
                </Card>

                <Card>
                  <CardHeader>
                    <h2 className="font-display font-semibold text-manejai-forest">Reprodução</h2>
                  </CardHeader>
                  <CardBody className="space-y-3 text-sm">
                    <Row
                      label="Taxa por diagnóstico"
                      value={formatRate(data?.reproduction.pregnancy_rate_by_diagnosis)}
                    />
                    <Row label="IEP mediano" value={formatDays(data?.reproduction.median_iep_days)} />
                    <Row
                      label="Diagnósticos positivos"
                      value={`${data?.reproduction.diagnoses_positive ?? 0}/${data?.reproduction.diagnoses_total ?? 0}`}
                    />
                  </CardBody>
                </Card>

                <Card className="lg:col-span-2">
                  <CardHeader>
                    <h2 className="font-display font-semibold text-manejai-forest">Top GMD no período</h2>
                  </CardHeader>
                  <CardBody className="p-0">
                    {(data?.top_gmd_animals.length ?? 0) === 0 ? (
                      <p className="px-5 py-8 text-sm text-center text-manejai-bark/55">
                        Sem pesagens suficientes para calcular GMD.
                      </p>
                    ) : (
                      <ul className="divide-y divide-manejai-sand/60">
                        {data!.top_gmd_animals.map((row) => (
                          <li key={row.animal_id} className="flex justify-between gap-3 px-5 py-3 text-sm">
                            <Link
                              to={`/animais/${row.animal_id}`}
                              className="text-manejai-moss hover:underline truncate"
                            >
                              {row.internal_number}
                              {row.name ? ` — ${row.name}` : ''}
                            </Link>
                            <span className="font-medium shrink-0">{formatGmd(row.gmd_kg_per_day)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </CardBody>
                </Card>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

function Metric({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Card>
      <CardBody className="text-center">
        <p className="text-xs text-manejai-bark/60">{label}</p>
        <p className="font-display text-2xl font-semibold text-manejai-forest">{value}</p>
        {hint && <p className="mt-1 text-xs text-manejai-bark/50">{hint}</p>}
      </CardBody>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-manejai-bark/65">{label}</span>
      <span className="font-medium text-manejai-forest">{value}</span>
    </div>
  );
}
